import throttle from 'lodash.throttle';
import { useState } from 'react';
import {
  ImageBackground,
  LayoutChangeEvent,
  LayoutRectangle,
  StyleSheet,
  View,
  ViewProps,
} from 'react-native';
import Svg, { Use, UseProps } from 'react-native-svg';

import FindNearMeButton from './FindNearMeButton';
import { SymbolId } from './SymbolsDefs';
import useAppNavigation from '../../hooks/useAppNavigation';
import { useMaxWidth } from '../../hooks/useResponsive';
import { PrimaryButton } from '../Button';
import { TextBodySmall, TextH4Big } from '../StyledText';
import { TransparentView } from '../Themed';

type Decor = UseProps & { href: `#${SymbolId}` };

export type HeaderSlideConfig = {
  bgColor: string;
  photoUrl: string;
  photoAltText?: string;
  title: string;
  description: string;
  decors?: Decor[];
  mainDecor?: Decor;
  cta?: {
    text: string;
    action: 'SEARCH' | 'PAGE_LINK';
    pageLink?: 'SUMMER_CAMPS' | 'BUY_GIFT_CARD';
  };
};

export const HeaderSlide = ({
  config,
  mobile,
  style,
  slideTextStyle,
  hidePhoto = false,
  photoStyle,
  additionalContent,
}: {
  config: HeaderSlideConfig;
  mobile: boolean;
  style?: ViewProps['style'];
  slideTextStyle?: ViewProps['style'];
  hidePhoto?: boolean;
  photoStyle?: ViewProps['style'];
  additionalContent?: React.ReactNode;
}) => {
  const isMobileDevice = useMaxWidth(800);
  const [layout, setLayout] = useState<LayoutRectangle | null>(null);
  const updateLayout = throttle((e: LayoutChangeEvent) => {
    setLayout(e.nativeEvent.layout);
  }, 100);

  const navigate = useAppNavigation();
  return (
    <TransparentView
      style={[
        styles.slide,
        styles.slideRow,
        mobile && styles.slideMobile,
        { backgroundColor: config.bgColor },
        style,
      ]}
    >
      <View
        style={[
          styles.slideItemText,
          isMobileDevice && styles.slideItemTextMobile,
          slideTextStyle,
        ]}
        onLayout={updateLayout}
      >
        <View style={{ zIndex: 2, gap: 10 }}>
          <TextH4Big>{config.title}</TextH4Big>
          <TextBodySmall>{config.description}</TextBodySmall>
          {additionalContent}
          {config.cta ? (
            config.cta.action === 'SEARCH' ? (
              <TransparentView style={styles.ctaButton}>
                <FindNearMeButton title={config.cta.text} />
              </TransparentView>
            ) : (
              <PrimaryButton
                title={config.cta.text}
                style={styles.ctaButton}
                onPress={() => {
                  if (config.cta?.action === 'PAGE_LINK') {
                    switch (config.cta?.pageLink) {
                      case 'SUMMER_CAMPS':
                        navigate.push('ProgramSummerCamps');
                        break;
                      case 'BUY_GIFT_CARD':
                        navigate.push('BuyGiftCard');
                        break;
                    }
                  }
                }}
              />
            )
          ) : null}
        </View>
        {config.decors && config.decors.length > 0 ? (
          <Svg
            viewBox={`0 0 ${layout?.width || 500} ${layout?.height || 400}`}
            style={{
              zIndex: 1,
              position: 'absolute',
              width: '100%',
              height: '100%',
              bottom: 0,
              right: 0,
            }}
          >
            {(isMobileDevice ? [config.decors[0]] : config.decors).map(
              (props, idx) => (
                <Use key={`decor-${idx}`} {...props} />
              )
            )}
          </Svg>
        ) : null}
      </View>
      {!hidePhoto && (
        <ImageBackground
          source={{ uri: config.photoUrl }}
          style={[
            styles.slideItemPhoto,
            mobile && styles.slideItemPhotoMobile,
            photoStyle,
          ]}
          resizeMode="cover"
        >
          {config.photoAltText ? (
            <View
              style={[
                styles.slideItemPhotoTextWrapper,
                mobile && styles.slideItemPhotoTextMobileWrapper,
              ]}
            >
              <View style={[styles.slideItemPhotoText]}>
                <TextBodySmall>
                  {ConvertToBold(config.photoAltText)}
                </TextBodySmall>
              </View>
            </View>
          ) : null}
        </ImageBackground>
      )}

      {isMobileDevice && config.mainDecor && (
        <TransparentView
          style={{
            width: 96,
            height: 96,
            position: 'absolute',
            right: 50,
            top: '50%',
            transform: [{ translateY: -48 }],
          }}
        >
          <Svg>
            <Use {...config.mainDecor} width={96} height={96} />
          </Svg>
        </TransparentView>
      )}
    </TransparentView>
  );
};

function ConvertToBold(text: string) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return (
        <TextBodySmall key={index} bold>
          {boldText}
        </TextBodySmall>
      );
    }
    return part;
  });
}

const styles = StyleSheet.create({
  ctaButton: {
    alignSelf: 'flex-start',
    marginTop: 30,
  },
  slide: {
    width: '100%',
    height: 400,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 10,
    overflow: 'hidden',
  },
  slideRow: {
    flexDirection: 'row',
  },
  slideMobile: {
    flexDirection: 'column',
    height: 'auto',
  },
  slideItemText: {
    flex: 1,
    alignSelf: 'stretch',
    padding: 50,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  slideItemTextMobile: {
    padding: 20,
    paddingTop: 50,
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
  },
  slideItemPhoto: {
    padding: 50,
    flex: 1,
    alignSelf: 'stretch',
    position: 'relative',
  },
  slideItemPhotoMobile: {
    height: 230,
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 230,
  },
  slideItemPhotoTextWrapper: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    alignItems: 'flex-end',
  },
  slideItemPhotoTextMobileWrapper: {
    left: 0,
    right: 'auto',
    width: '100%',
    paddingHorizontal: 10,
    alignItems: 'center',
  },
  slideItemPhotoText: {
    padding: 8,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
});
